import type { Breadcrumb, Event } from '@sentry/nextjs';

import { isIdentitySensitiveUrl } from '@/lib/kyc/privacy';

function isSensitiveString(value: unknown): boolean {
  return typeof value === 'string' && isIdentitySensitiveUrl(value);
}

function hasSensitiveUrlAttributes(data: Record<string, unknown> | undefined): boolean {
  return ['url', 'url.full', 'url.path', 'http.url', 'http.target'].some((key) => isSensitiveString(data?.[key]));
}

export function isIdentitySensitiveBreadcrumb(breadcrumb: Breadcrumb): boolean {
  return (
    isSensitiveString(breadcrumb.message) ||
    ['url', 'from', 'to'].some((key) => isSensitiveString(breadcrumb.data?.[key]))
  );
}

export function isIdentitySensitiveEvent(event: Event): boolean {
  // Inspect only telemetry fields: SDK processing metadata can contain circular live objects.
  const trace = event.contexts?.trace;

  return (
    isSensitiveString(event.request?.url) ||
    isSensitiveString(event.transaction) ||
    isSensitiveString(event.message) ||
    isSensitiveString(event.logentry?.message) ||
    isSensitiveString(trace?.description) ||
    hasSensitiveUrlAttributes(trace?.data) ||
    (event.spans?.some((span) => isSensitiveString(span?.description) || hasSensitiveUrlAttributes(span?.data)) ??
      false) ||
    (event.exception?.values?.some((exception) => isSensitiveString(exception?.value)) ?? false) ||
    (event.breadcrumbs?.some((breadcrumb) => breadcrumb != null && isIdentitySensitiveBreadcrumb(breadcrumb)) ?? false)
  );
}
